/**
 * PSP/CRE Tool v2.0 - Brigade Type HQ Position Offsets
 * 
 * Purpose: Define universal tactical doctrine for brigade type HQ positioning.
 * These offsets apply to ALL national standards and represent tactical positioning
 * requirements based on unit type, independent of national doctrine.
 * 
 * Author: Dr. Tatar Radu MD
 * Date: February 3, 2026
 * Version: 1.2 - UT-GAP-001: Added 8 missing UNIT_TYPES keys (March 16, 2026)
 *   BUG FIX: ARMOR, STRYKER, CAVALRY, ENGINEER, ENGINEERS, AIR_DEFENSE, SIGNAL, HQ
 *   were absent from BRIGADE_TYPE_HQ_OFFSETS. getBrigadeHQPosition() fell through to
 *   console.warn + base-position fallback for all 8 types.
 *   ARMOR/STRYKER/RECONNAISSANCE: 0 (maneuver/combat role — most forward)
 *   ENGINEER/ENGINEERS: +5 (close combat support — near but behind frontline)
 *   AIR_DEFENSE: +10 (covering element, behind main body; same category as AVIATION)
 *   SIGNAL: +20 (comms infrastructure requires rear-area protection; same as CSS)
 *   HQ: +20 (corps/division command post, precision-strike target, well behind FEBA)
 *   Also updated getAllBrigadeTypeOffsets() return array to include all 8 new types.
 *   Also updated console.warn valid types list.
 *
 * Version: 1.1 - Removed debug logging (February 13, 2026)
 * 
 * CHANGELOG:
 * v1.1 (Feb 13, 2026): Removed console.log debug output causing console noise
 *   - PROBLEM: getBrigadeHQPosition() called 12+ times, cluttering console
 *   - FIX: Removed if(NODE_ENV !== 'production') console.log statement
 *   - RESULT: Clean console output, same calculation accuracy
 *   - NOTE: Use getHQPositionBreakdown() for debugging position calculations
 * 
 * v1.0 (Feb 3, 2026): Initial implementation with national differentiation
 * 
 * ARCHITECTURE:
 * Brigade HQ Position = National Base Position + Unit Type Offset
 * 
 * - National Base Position: From nationalStandards.js (varies by nation)
 *   * Romanian: 15km (forward/aggressive posture)
 *   * NATO: 18km (balanced posture)
 *   * US: 20km (conservative/protected posture)
 * 
 * - Unit Type Offset: From this file (universal tactical doctrine)
 *   * Combat brigades: 0km (base position)
 *   * Artillery: +5km (indirect fire standoff)
 *   * CSS: +20km (supply dump protection)
 * 
 * EXAMPLE CALCULATIONS:
 * Romanian Artillery Brigade: 15km base + 5km offset = 20km
 * US Artillery Brigade: 20km base + 5km offset = 25km
 * NATO CSS Brigade: 18km base + 20km offset = 38km
 * 
 * DOCTRINAL RATIONALE:
 * 
 * Combat Brigades (0km offset):
 * - HEAVY, INFANTRY, MECHANIZED, MOUNTAIN, AIRBORNE, AIR_ASSAULT
 * - Direct engagement with enemy forces
 * - HQ positioned at base distance (most forward)
 * - Close to subordinate battalions for rapid C2
 * - Source: FM 3-96 Brigade Combat Team, ATP 3-21.8
 * 
 * Artillery Brigades (+5km offset):
 * - Indirect fire support role
 * - Needs standoff from direct fire threats
 * - Positioned behind combat brigades for protection
 * - Still within C2 range of supported units
 * - Source: FM 3-09 Field Artillery Operations
 * 
 * Aviation Brigades (+10km offset):
 * - Requires prepared surfaces (airfields, FARPs, landing zones)
 * - Needs protection from ground threats during flight ops
 * - Larger footprint for maintenance/refueling
 * - Must be behind front line for safe aircraft operations
 * - Source: FM 3-04 Army Aviation, ATP 3-04.1
 * 
 * CSS/Logistics Brigades (+20km offset):
 * - Large supply dumps and maintenance facilities
 * - High-value targets requiring protection
 * - Significant footprint (fuel, ammunition, supplies)
 * - Needs LOC access for resupply convoy operations
 * - Positioned well behind FEBA for survivability
 * - Source: ATP 4-90 Brigade Support Operations, FM 4-0 Sustainment
 * 
 * Medical Brigades (+15km offset):
 * - Provides Role 2 Enhanced and Role 3 medical support
 * - Needs protection (medical facilities are vulnerable)
 * - Must be accessible for casualty evacuation flow
 * - Balance: Protected but not too far (Golden Hour considerations)
 * - Source: AJMedP-1 NATO Medical Support, FM 4-02 Army Medical Support
 * 
 * HISTORICAL VALIDATION:
 * Ukraine 2022-2024 Combat Data:
 * - CSS units positioned 30-40km behind front survived better
 * - Forward CSS positions (15-20km) suffered high attrition from artillery
 * - Medical facilities at 20-30km achieved best casualty outcomes
 * - Artillery at 15-25km provided effective support with survivability
 * 
 * NATO Cold War Doctrine:
 * - Combat brigades: 10-20km from FEBA (depends on national posture)
 * - Artillery: +5-10km from combat brigades
 * - CSS: +20-30km from FEBA (beyond Soviet artillery range)
 * 
 * Modern Threat Environment:
 * - Tube artillery: 30-40km range
 * - Rocket artillery (MLRS/HIMARS): 70-80km range
 * - Long-range precision: 150-300km range
 * - UAV reconnaissance: Makes static positions vulnerable
 * - Requires graduated standoff: Combat → Artillery → CSS
 * 
 * References:
 * - FM 3-96: Brigade Combat Team (2015-2023)
 * - FM 3-09: Field Artillery Operations and Fire Support (2014)
 * - FM 3-04: Army Aviation (2015)
 * - ATP 4-90: Brigade Support Operations (2014)
 * - FM 4-02: Army Medical Support (2013)
 * - AJMedP-1: NATO Allied Joint Medical Support Doctrine
 * - Ukraine 2022-2024 casualty analysis (Dupuy Institute)
 * 
 * Usage:
 * import { BRIGADE_TYPE_HQ_OFFSETS, getBrigadeHQPosition } from '../constants/brigadeTypeOffsets';
 */

import { NATIONAL_STANDARDS } from './nationalStandards';

// ============================================================================
// BRIGADE TYPE HQ POSITION OFFSETS (km from base position)
// ============================================================================

/**
 * Universal tactical doctrine offsets for brigade type HQ positioning.
 * 
 * These offsets are added to the national base brigade HQ position
 * to determine the final HQ location for different brigade types.
 * 
 * Offsets represent tactical requirements based on unit role:
 * - Combat units: 0km (most forward, direct engagement)
 * - Support units: +5-20km (graduated standoff for protection)
 * 
 * @type {Object.<string, number>}
 */
export const BRIGADE_TYPE_HQ_OFFSETS = {
  // ========================================
  // COMBAT BRIGADES - Base Position (0km)
  // ========================================
  
  /**
   * Armored Brigade (Heavy)
   * Direct ground combat with tanks and armored vehicles
   * HQ at base position (most forward)
   */
  HEAVY: 0,
  
  /**
   * Infantry Brigade (Light)
   * Direct ground combat with dismounted infantry
   * HQ at base position (most forward)
   */
  INFANTRY: 0,
  
  /**
   * Mechanized Infantry Brigade
   * Direct ground combat with infantry fighting vehicles
   * HQ at base position (most forward)
   */
  MECHANIZED: 0,
  
  /**
   * Mountain Infantry Brigade
   * Specialized terrain operations
   * HQ at base position (most forward)
   */
  MOUNTAIN: 0,
  
  /**
   * Airborne Brigade
   * Air assault and airborne operations
   * HQ at base position (most forward)
   */
  AIRBORNE: 0,
  
  /**
   * Air Assault Brigade
   * Helicopter-borne air assault operations
   * HQ at base position (most forward)
   */
  AIR_ASSAULT: 0,
  
  // ========================================
  // FIRES BRIGADES - Behind Front (+5km)
  // ========================================
  
  /**
   * Field Artillery Brigade
   * Indirect fire support (155mm howitzers, MLRS)
   * Positioned +5km for standoff from direct fire
   * Still within C2 range of supported units
   * 
   * Rationale:
   * - Artillery provides indirect fire (15-30km range)
   * - Needs protection from direct fire threats
   * - +5km puts HQ beyond small arms/direct fire range
   * - Maintains effective C2 for fire missions
   */
  ARTILLERY: 5,
  
  // ========================================
  // MANEUVER SUPPORT - Behind Front (+10km)
  // ========================================
  
  /**
   * Combat Aviation Brigade
   * Attack helicopters, utility helicopters, UAVs
   * Positioned +10km for airfield/FARP operations
   * 
   * Rationale:
   * - Requires prepared surfaces for flight operations
   * - Needs larger footprint for maintenance/refueling
   * - +10km provides protected airspace for takeoff/landing
   * - Forward Arming and Refueling Points (FARPs) need space
   */
  AVIATION: 10,
  
  // ========================================
  // COMBAT SERVICE SUPPORT - Well Behind (+20km)
  // ========================================
  
  /**
   * Logistics Brigade (European/NATO style)
   * Supply, transportation, maintenance support
   * Positioned +20km for supply dump protection
   * 
   * Rationale:
   * - Large supply dumps (fuel, ammunition, food, water)
   * - High-value targets requiring maximum protection
   * - Significant footprint vulnerable to attack
   * - +20km puts beyond most tube artillery (30-40km range)
   * - Needs LOC access for resupply convoys
   */
  LOGISTICS: 20,
  
  /**
   * Sustainment Brigade (US style, post-2006)
   * Integrated CSS support (US modular transformation)
   * Same positioning requirements as LOGISTICS
   * 
   * Note: US Army renamed LOGISTICS to SUSTAINMENT in 2006
   * Tactical positioning requirements are identical
   */
  // SUSTAINMENT is an alias of LOGISTICS (2026-10-25): the same offset
  
  // ========================================
  // MEDICAL SUPPORT - Protected but Accessible (+15km)
  // ========================================
  
  /**
   * Medical Brigade
   * Role 2 Enhanced and Role 3 medical facilities
   * Positioned +15km for protection with accessibility
   * 
   * Rationale:
   * - Medical facilities are vulnerable (large, static)
   * - Must be protected from direct/indirect fire
   * - Must be accessible for casualty evacuation flow
   * - +15km balances protection with Golden Hour requirements
   * - Too far back: Evacuation times increase, more DOW
   * - Too far forward: Facility vulnerable to enemy fire
   * - 15km sweet spot validated by Ukraine 2022-2024 data
   */
  MEDICAL: 15,

  // ========================================
  // v1.2 FIX (UT-GAP-001): Previously missing types
  // ========================================

  /**
   * Armored Brigade (US-specific alias for HEAVY)
   * Same combat role — HQ at base position (most forward)
   */
  ARMOR: 0,

  /**
   * Stryker Brigade Combat Team (US medium-weight BCT)
   * Wheeled maneuver — same forward posture as other combat brigades
   */
  STRYKER: 0,

  /**
   * Reconnaissance Brigade (the cavalry of the US, other nations' reconnaissance regiments; key = the function)
   * Forward screening — HQ at base position (reconnaissance elements
   * may actually operate forward of FEBA; HQ stays at base)
   */
  RECONNAISSANCE: 0,

  /**
   * Combat Engineer Brigade (singular form)
   * Close combat support — breach, obstacle, mobility/counter-mobility
   * +5km: behind frontline but close enough for rapid task-force attachment
   */

  /**
   * Combat Engineer Brigade (2026-10-24: one key, ENGINEERS; the singular ENGINEER is a spelling of it)
   * Identical tactical positioning requirements
   */
  ENGINEERS: 5,

  /**
   * Air Defense Brigade
   * Positioned to provide air-defense coverage over the main body
   * +10km: similar to AVIATION — needs protected airspace, radar siting
   * Source: FM 3-01 Air and Missile Defense Operations
   */
  AIR_DEFENSE: 10,

  /**
   * Signal Brigade / Communications Brigade
   * Network infrastructure requires rear-area sites
   * +20km: co-located with CSS infrastructure for power and LOC access
   * Source: FM 6-02 Signal Support to Operations
   */
  SIGNAL: 20,

  /**
   * Headquarters Brigade / Corps-Division HQ Element
   * High-value precision-strike target — maximum protection required
   * +20km: positioned with CSS echelon well behind FEBA
   * Ukraine 2022–2024: HIMARS/drone targeting of command posts
   * Source: ADP 6-0 Command and Control; Ukraine combat data
   */
  HQ: 20
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get final HQ position for a brigade
 * 
 * Calculates: National Base Position + Unit Type Offset
 * 
 * @param {string} nationalStandard - National standard (ROMANIAN, NATO, US, MULTINATIONAL)
 * @param {string} brigadeType - Brigade type (HEAVY, ARTILLERY, LOGISTICS, etc.)
 * @returns {number} Final HQ position in km from FEBA
 * 
 * @throws {Error} If nationalStandard is unknown
 * @throws {Error} If brigadeType is not found in BRIGADE_TYPE_HQ_OFFSETS
 * 
 * @example
 * // Romanian Artillery Brigade
 * const position = getBrigadeHQPosition('ROMANIAN', 'ARTILLERY');
 * // Returns: 20 (15km base + 5km offset)
 * 
 * @example
 * // US CSS Brigade
 * const position = getBrigadeHQPosition('US', 'SUSTAINMENT');
 * // Returns: 40 (20km base + 20km offset)
 */
export function getBrigadeHQPosition(nationalStandard, brigadeType) {
  // Validate national standard
  const standard = NATIONAL_STANDARDS[nationalStandard];
  if (!standard) {
    throw new Error(
      `[brigadeTypeOffsets] Unknown national standard: "${nationalStandard}"\n` +
      `Valid options: ROMANIAN, NATO, US, MULTINATIONAL`
    );
  }
  
  // Get base position from national standard
  const basePosition = standard.brigade?.hq_position_feba_km;
  if (basePosition === undefined) {
    throw new Error(
      `[brigadeTypeOffsets] National standard "${nationalStandard}" missing brigade.hq_position_feba_km\n` +
      `Please update nationalStandards.js to v2.4 or higher`
    );
  }
  
  // Get type offset
  const typeOffset = BRIGADE_TYPE_HQ_OFFSETS[brigadeType];
  if (typeOffset === undefined) {
    // Unknown brigade type - log warning but don't throw
    // Use base position as fallback for forward compatibility
    console.warn(
      `[brigadeTypeOffsets] Unknown brigade type: "${brigadeType}"\n` +
      `Using base position (${basePosition}km) with no offset.\n` +
      `Valid types: HEAVY, INFANTRY, MECHANIZED, MOUNTAIN, AIRBORNE, AIR_ASSAULT, ` +
      `ARTILLERY, AVIATION, LOGISTICS, MEDICAL, ` +
      `ARMOR, STRYKER, RECONNAISSANCE, ENGINEERS, AIR_DEFENSE, SIGNAL, HQ`
    );
    return basePosition;
  }
  
  // Calculate final position
  const finalPosition = basePosition + typeOffset;
  
  // v1.1 FIX (Feb 13, 2026): Removed debug logging
  // PROBLEM: getBrigadeHQPosition() called 12+ times causing console noise
  // IMPACT: Performance overhead, cluttered console output
  // FIX: Removed console.log - position calculations are correct, just noisy
  // NOTE: Use getHQPositionBreakdown() for debugging if needed
  
  return finalPosition;
}

/**
 * Get HQ position breakdown for debugging/display
 * 
 * Returns detailed information about how HQ position was calculated.
 * Useful for UI display and debugging.
 * 
 * @param {string} nationalStandard - National standard
 * @param {string} brigadeType - Brigade type
 * @returns {Object} Detailed breakdown
 * 
 * @example
 * const breakdown = getHQPositionBreakdown('ROMANIAN', 'ARTILLERY');
 * // Returns:
 * // {
 * //   nationalStandard: 'ROMANIAN',
 * //   brigadeType: 'ARTILLERY',
 * //   basePosition: 15,
 * //   typeOffset: 5,
 * //   finalPosition: 20,
 * //   calculation: '15km (ROMANIAN base) + 5km (ARTILLERY offset) = 20km'
 * // }
 */
export function getHQPositionBreakdown(nationalStandard, brigadeType) {
  const standard = NATIONAL_STANDARDS[nationalStandard];
  const basePosition = standard?.brigade?.hq_position_feba_km || 15;
  const typeOffset = BRIGADE_TYPE_HQ_OFFSETS[brigadeType] || 0;
  const finalPosition = basePosition + typeOffset;
  
  return {
    nationalStandard,
    brigadeType,
    basePosition,
    typeOffset,
    finalPosition,
    calculation: `${basePosition}km (${nationalStandard} base) + ${typeOffset}km (${brigadeType} offset) = ${finalPosition}km`,
    source: {
      base: 'nationalStandards.js',
      offset: 'brigadeTypeOffsets.js'
    }
  };
}

/**
 * Get all brigade types and their offsets
 * 
 * Returns complete list of brigade types with offsets for documentation/UI.
 * 
 * @returns {Array<Object>} Array of brigade type info
 * 
 * @example
 * const types = getAllBrigadeTypeOffsets();
 * // Returns array of:
 * // [
 * //   { type: 'HEAVY', offset: 0, category: 'Combat' },
 * //   { type: 'ARTILLERY', offset: 5, category: 'Fires' },
 * //   { type: 'LOGISTICS', offset: 20, category: 'CSS' },
 * //   ...
 * // ]
 */
export function getAllBrigadeTypeOffsets() {
  return [
    // Combat brigades
    { type: 'HEAVY', offset: 0, category: 'Combat', description: 'Armored Brigade' },
    { type: 'INFANTRY', offset: 0, category: 'Combat', description: 'Infantry Brigade' },
    { type: 'MECHANIZED', offset: 0, category: 'Combat', description: 'Mechanized Infantry Brigade' },
    { type: 'MOUNTAIN', offset: 0, category: 'Combat', description: 'Mountain Infantry Brigade' },
    { type: 'AIRBORNE', offset: 0, category: 'Combat', description: 'Airborne Brigade' },
    { type: 'AIR_ASSAULT', offset: 0, category: 'Combat', description: 'Air Assault Brigade' },
    
    // Fires brigades
    { type: 'ARTILLERY', offset: 5, category: 'Fires', description: 'Field Artillery Brigade' },
    
    // Maneuver support
    { type: 'AVIATION', offset: 10, category: 'Maneuver Support', description: 'Combat Aviation Brigade' },
    
    // CSS brigades
    { type: 'LOGISTICS', offset: 20, category: 'CSS', description: 'Logistics Brigade (NATO/European)' },
    
    // Medical support
    { type: 'MEDICAL', offset: 15, category: 'Medical', description: 'Medical Brigade' },

    // v1.2: Previously missing types
    { type: 'ARMOR', offset: 0, category: 'Combat', description: 'Armored Brigade (US alias for HEAVY)' },
    { type: 'STRYKER', offset: 0, category: 'Combat', description: 'Stryker BCT (US medium-weight)' },
    { type: 'RECONNAISSANCE', offset: 0, category: 'Combat', description: 'Reconnaissance Brigade' },
    { type: 'ENGINEERS', offset: 5, category: 'Combat Support', description: 'Combat Engineer Brigade' },
    { type: 'AIR_DEFENSE', offset: 10, category: 'Combat Support', description: 'Air Defense Brigade' },
    { type: 'SIGNAL', offset: 20, category: 'CSS', description: 'Signal/Communications Brigade' },
    { type: 'HQ', offset: 20, category: 'CSS', description: 'Headquarters Element' }
  ];
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const brigadeTypeOffsets = {
  BRIGADE_TYPE_HQ_OFFSETS,
  getBrigadeHQPosition,
  getHQPositionBreakdown,
  getAllBrigadeTypeOffsets
};

export default brigadeTypeOffsets;
